import { useEffect, useState } from 'react';

import { autoFormaliseOffers } from '@api/autoFormaliseOffers';
import { tracking } from '@iwoca/frontend-tracking-library';
import IwRedirect from '@iwRouter/IwRedirect';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import { DocsComplete } from './DocsComplete';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import {
    CreateNewApplicationForm,
    TApplicationStatus
} from '../CreateNewApplicationForm/CreateNewApplicationForm';
import DrawdownPanel from '../DrawdownPanel/DrawdownPanel';
import { TopUpDeclined } from '../RequestMoreFundsPanel/TopUpStatusMessages/TopUpDeclined';
import TopUpStatusMessage from '../RequestMoreFundsPanel/TopUpStatusMessages/TopUpStatusMessage';

export const TopUpForm = ({
    formHeader,
    accountId
}: {
    formHeader?: React.ReactNode;
    accountId: string;
}) => {
    const [applicationStatus, setApplicationStatus] =
        useState<TApplicationStatus>(null);
    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    useEffect(() => {
        tracking.ampli.topupFormViewed({
            account_id: accountId,
            application_status: applicationStatus as string
        });
    }, [applicationStatus, accountId]);

    const handleSuccess = async () => {
        const { result } = await autoFormaliseOffers({
            stateKey
        });

        switch (result) {
            case 'FORMALISED':
                await queryClient.invalidateQueries({
                    queryKey: ['accounts']
                });
                await queryClient.invalidateQueries({
                    queryKey: ['application', stateKey]
                });
                setApplicationStatus('auto_approved');
                break;
            case 'DECLINED':
                await queryClient.invalidateQueries({
                    queryKey: ['application', stateKey]
                });
                setApplicationStatus('auto_declined');
                break;
            case 'DOCS_COMPLETE':
                setApplicationStatus('docs_complete');
                break;
            case 'DOCS_REQUIRED':
                setApplicationStatus('requires_documents');
                break;
        }
    };

    switch (applicationStatus) {
        case 'auto_approved':
            return <DrawdownPanel accountId={accountId} />;
        case 'auto_declined':
            return <TopUpDeclined />;
        case 'requires_documents':
            return (
                <IwRedirect
                    pathname="/apply-flexi-loan/"
                    searchString="topup=true"
                />
            );
        case 'docs_complete':
            return <DocsComplete />;
        case 'unknown':
            return (
                <TopUpStatusMessage
                    heading={
                        <FormattedMessage
                            id="_.outstanding_view.topup_form.errorHeading"
                            defaultMessage="Oops, it looks like something went wrong"
                        />
                    }
                    ctaText={
                        <FormattedMessage
                            id="_.outstanding_view.topup_form.errorCTA"
                            defaultMessage="Get in touch"
                        />
                    }
                />
            );
        default:
            return (
                <>
                    {formHeader}
                    <CreateNewApplicationForm
                        accountId={accountId}
                        setApplicationStatus={setApplicationStatus}
                        onSuccess={handleSuccess}
                    />
                </>
            );
    }
};
